import React from "react"; // Import React

import cx from "classnames";

import { Button } from "../Button";
import { ProfilePic } from "../ProfilePic";
import { Text } from "../Text";

import styles from "./UserDisplay.scss";

export interface UserProfile {
  /** First name of user */
  firstName?: string;
  /** User id */
  id?: string;
  /** Url of user image 30px in dimension */
  image30?: string;
  /** Url of user image 50px in dimension */
  image50?: string;
  /** Url of user image original size */
  image?: string;
  /** Initials of user */
  initials?: string;
  /** Job title of user */
  jobTitle?: string;
  /** Last name of user */
  lastName?: string;
  /** Full name of user */
  name?: string;
  /** Primary group of user */
  primaryGroup?: string;
}

export interface UserDisplayProps {
  /** User profile */
  userProfile: UserProfile;
  /** Function to be called when X is clicked */
  onClose?: (userProfile: UserProfile) => void;
  /** Text for remove button */
  removeText?: string;
  /** Selects curved styles for pill */
  pill?: boolean;
  /** Selects styles for detailed user display */
  withDetails?: boolean;
}

export function UserDisplay({
  userProfile,
  onClose,
  pill,
  withDetails,
  removeText,
}: UserDisplayProps) {
  const classNames = cx(styles["user-display"], {
    [styles["user-display--with-full-details"]]: withDetails,
    [styles["user-display--pill"]]: pill,
  });

  const displayDetailsClassName = cx(styles["user-display__details"], {
    [styles["user-display__details--without-close"]]: !onClose,
  });

  return (
    <div className={classNames} data-testid="user-display">
      <div className={styles["user-display__profile-pic__container"]}>
        <ProfilePic user={userProfile} dimensions={withDetails ? 40 : 22} />
      </div>
      <div className={displayDetailsClassName}>
        <div className={styles["user-display__details__container"]}>
          <Text className={styles["user-display__details__name"]}>
            {userProfile.name}
          </Text>
          {withDetails && userProfile.jobTitle && (
            <Text
              className={styles["user-display__details__title"]}
              size="extraSmall"
            >
              {userProfile.jobTitle}
            </Text>
          )}
        </div>
        {withDetails && userProfile.primaryGroup && (
          <div className={styles["user-display__details__primary-group"]}>
            {userProfile.primaryGroup}
          </div>
        )}
      </div>
      {onClose && (
        <div>
          <Button
            className={styles["user-display__close-button"]}
            data-testid="user-display-close-button"
            variant="transparent"
            onClick={() => onClose(userProfile)}
            size="small"
            icon="close"
          >
            <span className="screen-reader-only">{removeText ?? "Remove"}</span>
          </Button>
        </div>
      )}
    </div>
  );
}
